import React, { useMemo } from 'react';
import { Box, LinearProgress, Typography } from '@material-ui/core';
import { useGetList, useTranslate } from 'react-admin';
import { SOURCE_BOARDS_V2 } from '../../providers/resources';
import { extractIdFromURI } from '../../utils/resources/ids';
import { SourceBoardV2 } from '../../utils/types';
import InstallationFormsPanel from './InstallationFormsPanel';
import installationFromHistory from './installationFromHistory';
import useInstallationNotes from './useInstallationNotes';

// The line holds when a step took place (dateOfTakesPlace, as in the PDFs of the process). A note only knows when it was
// written, which can be much later, so its date is kept for an installation not yet on the board.
const STEP_DATES = ['readyToDeliverDate', 'poseDate', 'reserveLiftDate', 'productInstallationDate'];

/**
 * The forms of an avatar of /#/p/ancs, whose records do not carry them. They are read from its live history, over its
 * line of the v2 board: that line brings what the history does not hold (the capacities of the product type, the postal
 * code, the dates of the steps), but it follows the warehouse sync, so it may be behind the history or not exist yet.
 */
const AvatarFormsPanel = ({ avatarId, serialNumber }: { avatarId: string; serialNumber?: string }) => {
    const t = useTranslate();
    const { data, ids, loading, error }: any = useGetList(
        SOURCE_BOARDS_V2,
        { page: 1, perPage: 1 },
        { field: 'creationDate', order: 'DESC' },
        { avatarId: extractIdFromURI(avatarId) },
    );
    const { notes, loading: loadingNotes, error: notesError } = useInstallationNotes(avatarId);
    const line = ids?.length ? data?.[ids[0]] : undefined;

    const record = useMemo(() => {
        const stepDates = STEP_DATES.filter((field) => line?.[field]).map((field) => [field, line[field]]);
        return {
            productSerialNumber: serialNumber,
            ...line,
            avatarId,
            ...installationFromHistory(notes),
            ...Object.fromEntries(stepDates),
        } as SourceBoardV2;
    }, [line, avatarId, serialNumber, notes]);

    if (loading || loadingNotes) {
        return <LinearProgress />;
    }

    return (
        <>
            {(error || notesError) && (
                <Box style={{ padding: '12px 20px 0' }}>
                    <Typography variant='body2' color='error'>
                        {t('app.ancsV2.forms.loadError')}
                    </Typography>
                </Box>
            )}
            <InstallationFormsPanel record={record} />
        </>
    );
};

export default AvatarFormsPanel;
